import { useEffect, useRef, useState, useCallback } from "react";
import { SWIPE } from "@/lib/agendaSwipe";

// useWeekSwipe — gère le geste « changer de semaine » au défilement/glissé
// horizontal de la grille Agenda (Prompt 16).
// Responsabilités :
//  - capter le geste uniquement sur la carte de la grille (wheel non passif +
//    tactile), sans jamais bloquer le défilement vertical ;
//  - faire suivre légèrement la grille, cumuler jusqu'au seuil (120px) ;
//  - orchestrer l'animation sortie → chargement → entrée (un seul changement
//    par geste, verrouillage jusqu'à la fin) ;
//  - exposer l'état visuel (transform/opacité/transition, bulle, pastille) et
//    une méthode `go(dir)` réutilisée par les flèches ‹ › de la barre.
//
// Paramètres :
//  - cardRef : ref de la carte qui capte le geste ;
//  - onNavigate : async (dir) => charge et affiche la semaine visée (lève une
//    exception en cas d'échec de chargement) ;
//  - disabled : true pendant un glisser/étirer de RDV (geste désactivé) ;
//  - computeRange : (dir) => libellé de la semaine visée pour la bulle.
export function useWeekSwipe({ cardRef, onNavigate, disabled, computeRange }) {
  const [phase, setPhase] = useState("idle"); // idle|drag|reset|exit|wait|enterPrep|enter
  const [offset, setOffset] = useState(0);
  const [opacity, setOpacity] = useState(1);
  const [dir, setDir] = useState(1);
  const [progress, setProgress] = useState(0);
  const [showHint, setShowHint] = useState(false);
  const [showLoader, setShowLoader] = useState(false);

  const cumRef = useRef(0);
  const lockedRef = useRef(false);
  const committedRef = useRef(false);   // un changement déjà fait pendant ce geste
  const gestureEndTimer = useRef(null);  // détection de fin de geste (inactivité molette)
  const touchRef = useRef(null);

  // Toujours lire la dernière version des callbacks/flags sans réattacher les
  // écouteurs natifs à chaque rendu.
  const navRef = useRef(onNavigate); navRef.current = onNavigate;
  const disabledRef = useRef(disabled); disabledRef.current = disabled;
  const rangeRef = useRef(computeRange); rangeRef.current = computeRange;

  const wait = (ms) => new Promise((r) => setTimeout(r, ms));
  const twoFrames = () => new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

  // Retour doux à 0 (relâché avant le seuil).
  const resetDrag = useCallback(() => {
    cumRef.current = 0;
    setPhase("reset"); setOffset(0); setOpacity(1); setProgress(0); setShowHint(false);
    setTimeout(() => setPhase((p) => (p === "reset" ? "idle" : p)), 280);
  }, []);

  // Changement de semaine : sortie → chargement → entrée.
  const commit = useCallback(async (d) => {
    if (lockedRef.current || disabledRef.current) return;
    lockedRef.current = true;
    committedRef.current = true; // un seul changement par geste (jusqu'à la fin du flux)
    cumRef.current = 0;
    setDir(d); setProgress(1); setShowHint(true);

    // 1) Sortie dans le sens du geste (futur → gauche, passé → droite).
    setPhase("exit");
    setOffset(d === 1 ? -SWIPE.EXIT_PX : SWIPE.EXIT_PX);
    setOpacity(0);
    await wait(SWIPE.EXIT_MS);

    // 2) Chargement de la semaine visée (pastille si > 300 ms).
    setPhase("wait");
    const lt = setTimeout(() => setShowLoader(true), SWIPE.LOADER_DELAY);
    try {
      await navRef.current(d);
    } catch (e) {
      // Échec : la semaine précédente revient en douceur (toast géré en amont).
      clearTimeout(lt); setShowLoader(false);
      resetDrag();
      lockedRef.current = false;
      return;
    }
    clearTimeout(lt); setShowLoader(false);

    // 3) Entrée depuis le côté opposé, sans transition, puis vers 0.
    setPhase("enterPrep");
    setOffset(d === 1 ? SWIPE.ENTER_PX : -SWIPE.ENTER_PX);
    setOpacity(0);
    await twoFrames();
    setPhase("enter");
    setOffset(0); setOpacity(1);
    setTimeout(() => setShowHint(false), SWIPE.HINT_HIDE_AFTER_ENTER);
    await wait(SWIPE.ENTER_MS);
    setPhase("idle");
    lockedRef.current = false;
  }, [resetDrag]);

  // Cumul d'un delta horizontal (wheel ou tactile). Bloqué si un changement a
  // déjà eu lieu pendant ce geste (committedRef) ou pendant l'animation.
  const bump = useCallback((delta) => {
    if (lockedRef.current || committedRef.current || disabledRef.current) return;
    cumRef.current += delta;
    const cum = cumRef.current;
    const d = cum > 0 ? 1 : -1;
    const prog = Math.min(1, Math.abs(cum) / SWIPE.THRESHOLD);
    setDir(d); setProgress(prog); setShowHint(true); setPhase("drag"); setOpacity(1);
    setOffset(-cum * SWIPE.FOLLOW);
    if (Math.abs(cum) >= SWIPE.THRESHOLD) commit(d);
  }, [commit]);

  // Fin de geste (inactivité molette ou doigt levé) : on réautorise un
  // changement et, si le seuil n'a pas été atteint, on revient en douceur.
  const endGesture = useCallback(() => {
    gestureEndTimer.current = null;
    const wasCommitted = committedRef.current;
    cumRef.current = 0;
    committedRef.current = false;
    if (!wasCommitted && !lockedRef.current) resetDrag();
  }, [resetDrag]);

  // Écouteur molette/trackpad natif, non passif (pour pouvoir preventDefault).
  useEffect(() => {
    const el = cardRef.current; if (!el) return;
    const onWheel = (e) => {
      if (disabledRef.current) return;
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return; // vertical = défilement normal
      e.preventDefault(); e.stopPropagation();
      // Réarmer la détection de fin de geste (rafale/inertie trackpad) : tant que
      // des deltas horizontaux arrivent, le geste est considéré en cours.
      if (gestureEndTimer.current) clearTimeout(gestureEndTimer.current);
      gestureEndTimer.current = setTimeout(endGesture, SWIPE.RELEASE_MS);
      bump(e.deltaX);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [cardRef, bump, endGesture]);

  // Tactile : glissé horizontal = navigation ; vertical = défilement normal.
  useEffect(() => {
    const el = cardRef.current; if (!el) return;
    const onStart = (e) => {
      if (disabledRef.current || lockedRef.current) return;
      const t = e.touches[0];
      touchRef.current = { x: t.clientX, y: t.clientY, horiz: null, last: t.clientX };
    };
    const onMove = (e) => {
      const tc = touchRef.current; if (!tc || lockedRef.current || disabledRef.current) return;
      const t = e.touches[0];
      const dx = t.clientX - tc.x, dy = t.clientY - tc.y;
      if (tc.horiz === null) { if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return; tc.horiz = Math.abs(dx) > Math.abs(dy); }
      if (!tc.horiz) return; // geste vertical : laisser défiler
      e.preventDefault();
      const d = t.clientX - tc.last; tc.last = t.clientX;
      bump(-d); // doigt vers la gauche = futur (cohérent avec deltaX molette)
    };
    const onEnd = () => {
      const tc = touchRef.current; touchRef.current = null;
      if (tc && tc.horiz) endGesture();
    };
    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
    };
  }, [cardRef, bump, endGesture]);

  const transition = ({
    drag: "none",
    reset: "transform .25s cubic-bezier(.2,.8,.2,1), opacity .2s ease",
    exit: "transform .16s ease-in, opacity .16s ease-in",
    wait: "none",
    enterPrep: "none",
    enter: "transform .24s cubic-bezier(.2,.8,.2,1), opacity .24s cubic-bezier(.2,.8,.2,1)",
    idle: "none",
  })[phase] || "none";

  // transform:'none' au repos pour ne pas casser le sticky de l'en-tête des jours.
  const atRest = phase === "idle" && offset === 0;
  const trackStyle = {
    transform: atRest ? "none" : `translateX(${offset}px)`,
    opacity,
    transition,
    willChange: "transform, opacity",
  };

  return {
    trackStyle,
    showLoader,
    go: commit,
    hint: { show: showHint, dir, progress, range: rangeRef.current ? rangeRef.current(dir) : "" },
  };
}
